import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { closeTrade, closeOrphanPosition, cancelPendingOrder } from "../api/api";
import AccountSelection from "./AccountSelection";
import OpenPositions from "./OpenPositions";
import PendingOrders from "./PendingOrders";
import UpdateStopLossModal from "./UpdateStopLossModal";
import PartialCloseModal from "./PartialCloseModal";
import UpdateTakeProfitModal from "./UpdateTakeProfitModal";
import ConfirmModal from "./ui/ConfirmModal";
import DashboardWidgetCanvas from "./DashboardWidgetCanvas";
import DashboardActivityPanel from "./DashboardActivityPanel";
import { useAccountData } from "../context/AccountDataProvider";
import usePortfolioSocket from "../hooks/usePortfolioSocket";
import {
  canCloseOpenPosition,
  closeOpenPosition,
  closeOpenPositionsSequentially,
  getPositionCloseErrorMessage,
  resolveLinkedTradeId,
} from "../utils/positionClose";

const DashboardPinnedWidget = ({ title, description, className = "", children }) => (
  <section className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-borderDefault bg-backgroundSurface shadow-md ${className}`}>
    <div className="flex items-start justify-between gap-3 border-b border-borderDefault px-3 py-2.5">
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-textPrimary">{title}</div>
        {description && (
          <div className="mt-0.5 truncate text-[11px] text-textSecondary">{description}</div>
        )}
      </div>
    </div>
    <div className="min-h-0 flex-1 overflow-auto p-3">
      {children}
    </div>
  </section>
);

const Dashboard = () => {
  const { balance, equity, floatingPnl, currency, openPositions, pendingOrders, connect } = useAccountData();
  
  const [isUpdateSlModalOpen, setIsUpdateSlModalOpen] = useState(false);
  const [selectedTradeForSlUpdate, setSelectedTradeForSlUpdate] = useState(null);
  
  const [isPartialCloseModalOpen, setIsPartialCloseModalOpen] = useState(false); // State for PartialCloseModal
  const [selectedTradeForPartialClose, setSelectedTradeForPartialClose] = useState(null); // State for trade in PartialCloseModal

  const [isUpdateTpModalOpen, setIsUpdateTpModalOpen] = useState(false); // State for UpdateTakeProfitModal
  const [selectedTradeForTpUpdate, setSelectedTradeForTpUpdate] = useState(null); // State for trade in UpdateTakeProfitModal

  const [loadingTradeId, setLoadingTradeId] = useState(null);
  const [loadingOrderId, setLoadingOrderId] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState("");

  // Confirm modal state for close trade / cancel order
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'closeTrade'|'closeAllPositions'|'cancelOrder', id, position, positions }
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Portfolio (All Accounts) mode
  const [showPortfolio, setShowPortfolio] = useState(false);
  const [selectedIds, setSelectedIds] = useState(null); // null = all
  const { ready: portfolioReady, accountsById, positionsByAccount, pendingByAccount, lastEventAt, reconnect: reconnectPortfolio } = usePortfolioSocket({ accountIds: showPortfolio ? (selectedIds || undefined) : [], enabled: showPortfolio });

  const accountIds = useMemo(() => Object.keys(accountsById || {}), [accountsById]);
  const accountName = (id) => accountsById?.[id]?.name || id;
  const flattenedPositions = useMemo(() => {
    if (!showPortfolio) return [];
    const rows = [];
    const ids = selectedIds && selectedIds.length ? selectedIds : accountIds;
    for (const id of ids) {
      (positionsByAccount[id] || []).forEach((p) => rows.push({ ...p, accountId: id }));
    }
    return rows;
  }, [showPortfolio, positionsByAccount, selectedIds, accountIds]);
  const flattenedPending = useMemo(() => {
    if (!showPortfolio) return [];
    const rows = [];
    const ids = selectedIds && selectedIds.length ? selectedIds : accountIds;
    for (const id of ids) {
      (pendingByAccount[id] || []).forEach((o) => rows.push({ ...o, accountId: id }));
    }
    return rows;
  }, [showPortfolio, pendingByAccount, selectedIds, accountIds]);

  const visiblePortfolioAccountIds = useMemo(() => {
    if (!showPortfolio) return [];
    return selectedIds && selectedIds.length ? selectedIds : accountIds;
  }, [showPortfolio, selectedIds, accountIds]);

  const widgetPositions = useMemo(
    () => (showPortfolio ? flattenedPositions : openPositions),
    [showPortfolio, flattenedPositions, openPositions]
  );

  const widgetBalance = useMemo(() => {
    if (!showPortfolio) return balance;
    return visiblePortfolioAccountIds.reduce((sum, id) => sum + (Number(accountsById?.[id]?.balance) || 0), 0);
  }, [showPortfolio, balance, visiblePortfolioAccountIds, accountsById]);

  const widgetEquity = useMemo(() => {
    if (!showPortfolio) return equity;
    return visiblePortfolioAccountIds.reduce((sum, id) => sum + (Number(accountsById?.[id]?.equity) || 0), 0);
  }, [showPortfolio, equity, visiblePortfolioAccountIds, accountsById]);

  const formatPL = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n.toFixed(2) : '—';
  };

  const dashboardHeaderCellClassName = 'border-b widget-divider-border widget-header-fill px-2.5 py-2 text-[10px] font-medium uppercase tracking-[0.06em] text-textSecondary';
  const dashboardBodyCellClassName = 'border-b widget-divider-border px-2.5 py-2 align-middle text-xs text-textPrimary sm:text-sm';
  const dashboardTextButtonClassName = 'inline-flex items-center justify-center rounded-md border widget-muted-border bg-backgroundSurface/35 px-2.5 py-1 text-[11px] font-medium text-textSecondary transition hover:bg-secondary/10 hover:text-textPrimary';
  const dashboardDangerButtonClassName = 'inline-flex items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-1 text-[11px] font-medium text-destructive transition hover:bg-destructive/15';
  const dashboardFilterButtonClassName = 'inline-flex items-center justify-center rounded-md border widget-muted-border bg-backgroundSurface/30 px-2.5 py-1 text-[11px] font-medium text-textSecondary transition hover:bg-secondary/10 hover:text-textPrimary';

  // Currency symbol for display (from IB/broker account info)
  const currencySymbol = currency && currency !== 'USD' ? `${currency} ` : '$';

  useEffect(() => {
    if (showPortfolio) {
      // In portfolio mode, no single-account connection needed
      return;
    }
    if (selectedAccountId) {
      connect(selectedAccountId);
    }
    // No disconnect on cleanup — connection persists across page navigation
  }, [selectedAccountId, connect, showPortfolio]);

  // Opens confirm modal instead of window.confirm
  const handleCloseTrade = (tradeId, position) => {
    setConfirmAction({ type: 'closeTrade', id: tradeId || resolveLinkedTradeId(position), position });
  };

  const handleCloseAllPositions = (positions) => {
    const list = Array.isArray(positions) ? positions : [];
    if (list.length === 0) return;
    setConfirmAction({ type: 'closeAllPositions', positions: list });
  };

  const handleCancelOrder = (orderId) => {
    setConfirmAction({ type: 'cancelOrder', id: orderId });
  };

  // Executes the confirmed action
  const executeConfirmAction = async () => {
    if (!confirmAction) return;
    setConfirmLoading(true);
    try {
      if (confirmAction.type === 'closeTrade') {
        const loadingId = confirmAction.id || confirmAction.position?.ticket;
        setLoadingTradeId(loadingId);
        await closeOpenPosition(confirmAction.position, {
          closeTradeApi: closeTrade,
          closeOrphanPositionApi: closeOrphanPosition,
          fallbackAccountId: selectedAccountId,
          tradeId: confirmAction.id,
        });
        toast.success(confirmAction.id ? 'Trade closed successfully' : 'Broker position close submitted');
      } else if (confirmAction.type === 'closeAllPositions') {
        const positions = Array.isArray(confirmAction.positions) ? confirmAction.positions : [];
        setLoadingTradeId('__bulk_close_positions__');
        const result = await closeOpenPositionsSequentially(positions, {
          closeTradeApi: closeTrade,
          closeOrphanPositionApi: closeOrphanPosition,
          fallbackAccountId: selectedAccountId,
        });

        if (result.failures.length > 0) {
          const firstError = getPositionCloseErrorMessage(result.failures[0].error);
          toast.error(`Closed ${result.successes.length}/${result.total} positions. ${result.failures.length} failed. ${firstError}`);
        } else {
          toast.success(`Close submitted for ${result.successes.length} positions`);
        }
      } else if (confirmAction.type === 'cancelOrder') {
        setLoadingOrderId(confirmAction.id);
        await cancelPendingOrder(confirmAction.id);
        toast.success('Order cancelled successfully');
      }
    } catch (err) {
      const msg = confirmAction.type === 'cancelOrder'
        ? 'Failed to cancel order'
        : 'Failed to close position';
      toast.error(`${msg}. ${getPositionCloseErrorMessage(err)}`);
    } finally {
      setConfirmLoading(false);
      setLoadingTradeId(null);
      setLoadingOrderId(null);
      setConfirmAction(null);
    }
  };

  const handleOpenUpdateSlModal = (trade) => {
    setSelectedTradeForSlUpdate(trade);
    setIsUpdateSlModalOpen(true);
  };

  const handleCloseUpdateSlModal = () => {
    setIsUpdateSlModalOpen(false);
    setSelectedTradeForSlUpdate(null);
  };
  
  const handleOpenPartialCloseModal = (trade) => {
    setSelectedTradeForPartialClose(trade);
    setIsPartialCloseModalOpen(true);
  };

  const handleClosePartialCloseModal = () => {
    setIsPartialCloseModalOpen(false);
    setSelectedTradeForPartialClose(null);
  };

  const handleOpenUpdateTpModal = (trade) => {
    setSelectedTradeForTpUpdate(trade);
    setIsUpdateTpModalOpen(true);
  };

  const handleCloseUpdateTpModal = () => {
    setIsUpdateTpModalOpen(false);
    setSelectedTradeForTpUpdate(null);
  };

  // Generic success handler for SL, TP, Partial Close to refresh positions
  const handleTradeActionSuccess = async () => {
    if (isUpdateSlModalOpen) handleCloseUpdateSlModal();
    if (isPartialCloseModalOpen) handleClosePartialCloseModal();
    if (isUpdateTpModalOpen) handleCloseUpdateTpModal();
    // The open positions will be updated automatically by the WebSocket
  };


  // Computed stats from live data
  // Prefer account-level floating P/L (from IB) when available for consistency
  // with balance/equity. Fall back to summing individual position profits.
  const openPL = useMemo(() => {
    if (floatingPnl !== null && floatingPnl !== undefined) return floatingPnl;
    if (!openPositions || openPositions.length === 0) return null;
    const sum = openPositions.reduce((acc, p) => acc + (parseFloat(p.profit) || 0), 0);
    return sum;
  }, [floatingPnl, openPositions]);

  const closeAllConfirmCount = Array.isArray(confirmAction?.positions) ? confirmAction.positions.length : 0;
  const confirmTitle = confirmAction?.type === 'closeAllPositions'
    ? 'Close All Positions'
    : confirmAction?.type === 'closeTrade'
      ? (confirmAction?.id ? 'Close Trade' : 'Close Broker Position')
      : 'Cancel Order';
  const confirmMessage = confirmAction?.type === 'closeAllPositions'
    ? `Close all ${closeAllConfirmCount} visible open positions? This submits market close orders one by one.`
    : confirmAction?.type === 'closeTrade'
      ? 'Are you sure you want to close this position? This action cannot be undone.'
      : 'Are you sure you want to cancel this pending order?';
  const confirmLabel = confirmAction?.type === 'closeAllPositions'
    ? 'Close All'
    : confirmAction?.type === 'closeTrade'
      ? (confirmAction?.id ? 'Close Trade' : 'Close Position')
      : 'Cancel Order';

  const renderOpenPositionsWidget = () => {
    if (!showPortfolio) {
      return (
        <OpenPositions
          bare
          openPositions={openPositions}
          closeTrade={handleCloseTrade}
          closeAllPositions={handleCloseAllPositions}
          updateStopLoss={handleOpenUpdateSlModal}
          updateTakeProfit={handleOpenUpdateTpModal}
          partialClose={handleOpenPartialCloseModal}
          loadingTradeId={loadingTradeId}
          bulkCloseLoading={confirmLoading && confirmAction?.type === 'closeAllPositions'}
          fallbackAccountId={selectedAccountId}
        />
      );
    }

    return (
      <div className="w-full space-y-3">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex flex-wrap items-center justify-end gap-2">
              {flattenedPositions.length > 0 && (
                <button
                  type="button"
                  className={`${dashboardDangerButtonClassName} ${confirmLoading && confirmAction?.type === 'closeAllPositions' ? 'opacity-50 cursor-not-allowed' : ''}`}
                  onClick={() => handleCloseAllPositions(flattenedPositions)}
                  disabled={confirmLoading && confirmAction?.type === 'closeAllPositions'}
                  title="Close all visible open positions"
                >
                  {confirmLoading && confirmAction?.type === 'closeAllPositions' ? 'Closing...' : 'Close All'}
                </button>
              )}
              <div className="inline-flex items-center rounded-full border widget-muted-border bg-backgroundSurface/30 px-3 py-1 text-[11px] font-medium text-textSecondary">{flattenedPositions.length} rows</div>
            </div>
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-textSecondary">Filter Accounts</span>
              <button className={`${dashboardFilterButtonClassName} ${!selectedIds ? 'opacity-50 cursor-not-allowed' : ''}`} onClick={() => setSelectedIds(null)} disabled={!selectedIds}>Clear</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {accountIds.map((id) => (
                <label key={id} className="flex items-center gap-2 rounded-lg border widget-muted-border bg-backgroundSurface/20 px-3 py-2 hover:bg-secondary/10 cursor-pointer text-sm text-textPrimary transition-colors">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-borderDefault bg-backgroundSurface text-primary focus:ring-primary focus:ring-offset-0"
                    checked={selectedIds ? selectedIds.includes(id) : true}
                    onChange={() => setSelectedIds((prev) => {
                      if (!prev) return [id];
                      return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
                    })}
                  />
                  <span className="truncate font-medium">{accountName(id)}</span>
                  <span className="ml-auto text-[11px] text-textSecondary">{accountsById[id]?.platform}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-xl border widget-muted-border bg-backgroundSurface/20 overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse text-xs sm:text-sm">
            <thead>
              <tr>
                <th className={dashboardHeaderCellClassName}>Account</th>
                <th className={dashboardHeaderCellClassName}>Symbol</th>
                <th className={dashboardHeaderCellClassName}>Side</th>
                <th className={dashboardHeaderCellClassName}>Lots</th>
                <th className={dashboardHeaderCellClassName}>Open</th>
                <th className={dashboardHeaderCellClassName}>SL</th>
                <th className={dashboardHeaderCellClassName}>TP</th>
                <th className={`${dashboardHeaderCellClassName} text-right`}>P/L</th>
                <th className={dashboardHeaderCellClassName}>Time</th>
                <th className={dashboardHeaderCellClassName}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {flattenedPositions.length === 0 ? (
                <tr>
                  <td className="px-3 py-10 text-center text-sm text-textSecondary" colSpan={10}>No positions</td>
                </tr>
              ) : (
                flattenedPositions.map((p, i) => {
                  const linkedTradeId = resolveLinkedTradeId(p);
                  const canClose = canCloseOpenPosition(p);
                  const isBulkClosing = confirmLoading && confirmAction?.type === 'closeAllPositions';
                  const isLoadingThis = loadingTradeId === linkedTradeId || loadingTradeId === p.ticket || isBulkClosing;
                  return (
                    <tr key={`${p.accountId}-${p.ticket || i}`} className="hover:bg-secondary/10">
                      <td className={`${dashboardBodyCellClassName} truncate max-w-[100px] font-medium`}>{accountName(p.accountId)}</td>
                      <td className={`${dashboardBodyCellClassName} font-medium`}>{p.symbol}</td>
                      <td className={`${dashboardBodyCellClassName}`}>
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${p.direction === 'BUY' ? 'border-success/25 bg-success/10 text-success' : 'border-destructive/25 bg-destructive/10 text-destructive'}`}>{p.direction}</span>
                      </td>
                      <td className={`${dashboardBodyCellClassName} font-mono`}>{p.volume ?? '—'}</td>
                      <td className={`${dashboardBodyCellClassName} font-mono`}>{p.price_open ?? '—'}</td>
                      <td className={`${dashboardBodyCellClassName} font-mono`}>{p.sl ?? '—'}</td>
                      <td className={`${dashboardBodyCellClassName} font-mono`}>{p.tp ?? '—'}</td>
                      <td className={`${dashboardBodyCellClassName} text-right font-mono font-medium ${parseFloat(p.profit) >= 0 ? 'text-success' : 'text-destructive'}`}>{formatPL(p.profit)}</td>
                      <td className={`${dashboardBodyCellClassName} whitespace-nowrap text-textSecondary`}>{p.time ? new Date(p.time * 1000).toLocaleString() : '—'}</td>
                      <td className={dashboardBodyCellClassName}>
                        <div className="flex flex-wrap gap-1 sm:gap-2">
                          <button
                            className={`${dashboardDangerButtonClassName} ${isLoadingThis || !canClose ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={!canClose || isLoadingThis}
                            title={linkedTradeId ? 'Close trade' : 'Close broker position'}
                            onClick={() => handleCloseTrade(linkedTradeId, p)}
                          >
                            {isLoadingThis ? '...' : 'Close'}
                          </button>
                          <button
                            className={dashboardTextButtonClassName}
                            disabled={!p || isBulkClosing}
                            onClick={() => handleOpenUpdateSlModal(p)}
                          >
                            SL
                          </button>
                          <button
                            className={dashboardTextButtonClassName}
                            disabled={!p || isBulkClosing}
                            onClick={() => handleOpenUpdateTpModal(p)}
                          >
                            TP
                          </button>
                          <button
                            className={dashboardTextButtonClassName}
                            disabled={!p || isBulkClosing}
                            onClick={() => handleOpenPartialCloseModal(p)}
                          >
                            Part
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderPendingOrdersWidget = () => {
    if (!showPortfolio) {
      return (
        <PendingOrders
          bare
          pendingOrders={pendingOrders}
          cancelOrder={handleCancelOrder}
          loadingOrderId={loadingOrderId}
        />
      );
    }

    return (
      <div className="w-full space-y-3">
        <div className="flex justify-end">
          <div className="inline-flex items-center rounded-full border widget-muted-border bg-backgroundSurface/30 px-3 py-1 text-[11px] font-medium text-textSecondary">{flattenedPending.length} rows</div>
        </div>
        <div className="rounded-xl border widget-muted-border bg-backgroundSurface/20 overflow-x-auto">
          <table className="w-full min-w-[800px] border-collapse text-xs sm:text-sm">
            <thead>
              <tr>
                <th className={dashboardHeaderCellClassName}>Account</th>
                <th className={dashboardHeaderCellClassName}>Symbol</th>
                <th className={dashboardHeaderCellClassName}>Type</th>
                <th className={dashboardHeaderCellClassName}>Lots</th>
                <th className={dashboardHeaderCellClassName}>Price</th>
                <th className={dashboardHeaderCellClassName}>SL</th>
                <th className={dashboardHeaderCellClassName}>TP</th>
                <th className={dashboardHeaderCellClassName}>Time</th>
                <th className={dashboardHeaderCellClassName}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {flattenedPending.length === 0 ? (
                <tr>
                  <td className="px-3 py-10 text-center text-sm text-textSecondary" colSpan={9}>No pending orders</td>
                </tr>
              ) : (
                flattenedPending.map((o, i) => (
                  <tr key={`${o.accountId}-${o.order_id || i}`} className="hover:bg-secondary/10">
                    <td className={`${dashboardBodyCellClassName} truncate max-w-[100px] font-medium`}>{accountName(o.accountId)}</td>
                    <td className={`${dashboardBodyCellClassName} font-medium`}>{o.symbol}</td>
                    <td className={dashboardBodyCellClassName}>{o.type || o.order_type}</td>
                    <td className={`${dashboardBodyCellClassName} font-mono`}>{o.volume ?? '—'}</td>
                    <td className={`${dashboardBodyCellClassName} font-mono`}>{o.price ?? o.price_open ?? '—'}</td>
                    <td className={`${dashboardBodyCellClassName} font-mono`}>{o.sl ?? '—'}</td>
                    <td className={`${dashboardBodyCellClassName} font-mono`}>{o.tp ?? '—'}</td>
                    <td className={`${dashboardBodyCellClassName} whitespace-nowrap text-textSecondary`}>{o.time ? new Date(o.time * 1000).toLocaleString() : '—'}</td>
                    <td className={dashboardBodyCellClassName}>
                      <button
                        className={`${dashboardDangerButtonClassName} ${loadingOrderId === o.order_id ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={!o?.order_id || loadingOrderId === o.order_id}
                        title={o?.order_id ? 'Cancel pending order' : 'Missing order id'}
                        onClick={() => handleCancelOrder(o.order_id)}
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderBalanceWidget = () => {
    if (!showPortfolio) {
      return (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
          <div>
            <p className="text-xs sm:text-sm text-textSecondary">Balance</p>
            <p className="text-lg sm:text-2xl font-bold text-success">{typeof balance === 'number' ? `${currencySymbol}${balance.toFixed(2)}` : '-'}</p>
          </div>
          <div>
            <p className="text-xs sm:text-sm text-textSecondary">Equity</p>
            <p className="text-lg sm:text-2xl font-bold text-success">{typeof equity === 'number' ? `${currencySymbol}${equity.toFixed(2)}` : '-'}</p>
          </div>
          <div>
            <p className="text-xs sm:text-sm text-textSecondary">Floating P/L</p>
            <p className={`text-lg sm:text-2xl font-bold ${openPL !== null && openPL >= 0 ? 'text-success' : 'text-destructive'}`}>
              {openPL !== null ? `${currencySymbol}${openPL.toFixed(2)}` : '-'}
            </p>
          </div>
          <div>
            <p className="text-xs sm:text-sm text-textSecondary">Open / Pending</p>
            <p className="text-lg sm:text-2xl font-bold text-primary">
              {openPositions?.length ?? 0} / {pendingOrders?.length ?? 0}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className="text-xs sm:text-sm text-textSecondary">Last event: {lastEventAt ? new Date(lastEventAt).toLocaleTimeString() : '-'}</div>
        <div className="mt-4 grid grid-cols-1 gap-3 text-left sm:grid-cols-2 xl:grid-cols-3">
          {accountIds.map((id) => {
            const acc = accountsById[id] || {};
            const fmt2 = (n) => (typeof n === 'number' ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '-');
            return (
              <div key={id} className="rounded border border-borderDefault p-3">
                <div className="flex items-center justify-between">
                  <div className="truncate text-sm font-semibold sm:text-base">{accountName(id)}</div>
                  <div className="text-xs text-textSecondary">{acc.platform}</div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:text-sm">
                  <div className="text-textSecondary">Balance</div>
                  <div className="text-success">{fmt2(acc.balance)}</div>
                  <div className="text-textSecondary">Equity</div>
                  <div className="text-success">{fmt2(acc.equity)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const dashboardHeaderContent = (
    <div className="flex min-w-0 flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center">
      {!showPortfolio && (
        <div className="flex min-w-0 flex-col gap-2 rounded-lg bg-backgroundBody/45 px-2 py-1.5 sm:flex-row sm:items-center">
          <div className="w-full sm:w-auto sm:min-w-[220px]">
            <AccountSelection
              selectedAccount={selectedAccountId}
              setSelectedAccount={setSelectedAccountId}
              displayMode="compact"
              preferDefaultOnInitialLoad
            />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-borderDefault bg-backgroundBody/45 px-2 py-1.5">
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium ${!showPortfolio ? 'text-primary' : 'text-textSecondary'}`}>Single</span>
          <button
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 focus:ring-offset-backgroundSurface ${
              showPortfolio ? 'bg-primary' : 'bg-gray-600'
            }`}
            onClick={() => setShowPortfolio(!showPortfolio)}
            role="switch"
            aria-checked={showPortfolio}
            aria-label="Toggle portfolio mode"
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                showPortfolio ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
          <span className={`text-xs font-medium ${showPortfolio ? 'text-primary' : 'text-textSecondary'}`}>All Accounts</span>
        </div>
        {showPortfolio && (
          <>
            <span className={`rounded-full px-2 py-1 text-xs font-medium ${portfolioReady ? 'bg-success/20 text-success' : 'bg-destructive/20 text-destructive'}`}>{portfolioReady ? 'Live' : 'Connecting...'}</span>
            <button className="btn btn-neutral btn-xs sm:btn-sm" onClick={reconnectPortfolio}>Reconnect</button>
          </>
        )}
      </div>
    </div>
  );

  const dashboardMobileHeaderContent = (
    <div className="flex min-w-0 flex-1 items-center gap-1.5">
      {!showPortfolio && (
        <div className="min-w-0 flex-1">
          <AccountSelection
            selectedAccount={selectedAccountId}
            setSelectedAccount={setSelectedAccountId}
            displayMode="compact"
            className="w-full"
            selectClassName="w-full"
            buttonClassName="py-1.5 pl-2 pr-8 text-xs"
            dropdownMinWidth={330}
            showFullOptionLabels
            preferDefaultOnInitialLoad
          />
        </div>
      )}

      <div className="flex shrink-0 items-center gap-1 rounded-lg border border-borderDefault bg-backgroundBody/45 px-1 py-1.5">
        <span className={`text-[10px] font-medium ${!showPortfolio ? 'text-primary' : 'text-textSecondary'}`}>Single</span>
        <button
          className={`relative inline-flex h-5 w-8 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 focus:ring-offset-backgroundSurface ${
            showPortfolio ? 'bg-primary' : 'bg-gray-600'
          }`}
          onClick={() => setShowPortfolio(!showPortfolio)}
          role="switch"
          aria-checked={showPortfolio}
          aria-label="Toggle portfolio mode"
        >
          <span
            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              showPortfolio ? 'translate-x-3' : 'translate-x-0'
            }`}
          />
        </button>
        <span className={`whitespace-nowrap text-[10px] font-medium ${showPortfolio ? 'text-primary' : 'text-textSecondary'}`}>All</span>
      </div>
    </div>
  );

  const dashboardPinnedContent = (
    <>
      <div className="grid w-full items-start gap-4 xl:grid-cols-[340px_minmax(0,1fr)]">
        <DashboardPinnedWidget
          title="Needs Review"
          description="Pending approvals and important alerts."
        >
          <DashboardActivityPanel bare />
        </DashboardPinnedWidget>

        <DashboardPinnedWidget
          title={showPortfolio ? 'Account Balances' : 'Balance'}
          description={showPortfolio ? 'Connected account balances and equity.' : 'Selected account balance, equity, P/L, and exposure.'}
        >
          {renderBalanceWidget()}
        </DashboardPinnedWidget>
      </div>

      <div className="grid w-full gap-4">
        <DashboardPinnedWidget
          title={showPortfolio ? 'All Accounts - Open Positions' : 'Open Positions'}
          description="Live positions and trade-management actions."
        >
          {renderOpenPositionsWidget()}
        </DashboardPinnedWidget>

        <DashboardPinnedWidget
          title={showPortfolio ? 'All Accounts - Pending Orders' : 'Pending Orders'}
          description="Orders waiting to trigger."
        >
          {renderPendingOrdersWidget()}
        </DashboardPinnedWidget>
      </div>
    </>
  );


  return (
    <div className="min-h-screen flex flex-col bg-backgroundBody text-textPrimary overflow-x-hidden">
      <main className="flex-1 p-3 sm:p-6 max-w-full space-y-4 sm:space-y-6">
        {false && (
        <>

        {/* ── Stats Header ─────────────────────────────────────── */}
        <div className="grid w-full items-start gap-4 xl:grid-cols-[340px_minmax(0,1fr)]">
          <DashboardPinnedWidget
            title="Needs Review"
            description="Pending approvals and important alerts."
            className="xl:order-1"
          >
            <DashboardActivityPanel bare />
          </DashboardPinnedWidget>

          <div className="bg-backgroundSurface p-4 sm:p-5 shadow-md rounded-xl xl:order-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-primary">
                {showPortfolio ? 'Portfolio Overview' : 'Dashboard'}
              </h2>
              <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2 sm:gap-3">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium ${!showPortfolio ? 'text-primary' : 'text-textSecondary'}`}>Single</span>
                  <button
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 focus:ring-offset-backgroundSurface ${
                      showPortfolio ? 'bg-primary' : 'bg-gray-600'
                    }`}
                    onClick={() => setShowPortfolio(!showPortfolio)}
                    role="switch"
                    aria-checked={showPortfolio}
                    aria-label="Toggle portfolio mode"
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        showPortfolio ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <span className={`text-xs font-medium ${showPortfolio ? 'text-primary' : 'text-textSecondary'}`}>All Accounts</span>
                </div>
                {showPortfolio && (
                  <>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${portfolioReady ? 'bg-success/20 text-success' : 'bg-destructive/20 text-destructive'}`}>{portfolioReady ? '● Live' : '○ Connecting…'}</span>
                    <button className="btn btn-neutral btn-xs sm:btn-sm" onClick={reconnectPortfolio}>Reconnect</button>
                  </>
                )}
              </div>
            </div>

            {!showPortfolio && (
              <div className="mb-4 flex flex-col gap-2 rounded-lg border border-borderDefault bg-backgroundBody/45 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-xs font-medium uppercase tracking-[0.16em] text-textSecondary">Account</div>
                  <div className="mt-1 text-sm text-textPrimary">Choose which account feeds the dashboard.</div>
                </div>
                <div className="w-full sm:w-auto sm:min-w-[220px]">
                  <AccountSelection
                    selectedAccount={selectedAccountId}
                    setSelectedAccount={setSelectedAccountId}
                    displayMode="compact"
                  />
                </div>
              </div>
            )}

            {!showPortfolio ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                <div className="text-center">
                  <p className="text-xs sm:text-sm text-textSecondary">Balance</p>
                  <p className="text-lg sm:text-2xl font-bold text-success">{typeof balance === 'number' ? `${currencySymbol}${balance.toFixed(2)}` : '—'}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs sm:text-sm text-textSecondary">Equity</p>
                  <p className="text-lg sm:text-2xl font-bold text-success">{typeof equity === 'number' ? `${currencySymbol}${equity.toFixed(2)}` : '—'}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs sm:text-sm text-textSecondary">Floating P/L</p>
                  <p className={`text-lg sm:text-2xl font-bold ${openPL !== null && openPL >= 0 ? 'text-success' : 'text-destructive'}`}>
                    {openPL !== null ? `${currencySymbol}${openPL.toFixed(2)}` : '—'}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs sm:text-sm text-textSecondary">Open / Pending</p>
                  <p className="text-lg sm:text-2xl font-bold text-primary">
                    {openPositions?.length ?? 0} / {pendingOrders?.length ?? 0}
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="text-xs sm:text-sm text-textSecondary">Last event: {lastEventAt ? new Date(lastEventAt).toLocaleTimeString() : '—'}</div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 text-left">
                  {accountIds.map((id) => {
                    const acc = accountsById[id] || {};
                    const fmt2 = (n) => (typeof n === 'number' ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—');
                    return (
                      <div key={id} className="p-3 rounded border border-borderDefault">
                        <div className="flex items-center justify-between">
                          <div className="font-semibold text-sm sm:text-base truncate">{accountName(id)}</div>
                          <div className="text-xs text-textSecondary">{acc.platform}</div>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:text-sm">
                          <div className="text-textSecondary">Balance</div>
                          <div className="text-success">{fmt2(acc.balance)}</div>
                          <div className="text-textSecondary">Equity</div>
                          <div className="text-success">{fmt2(acc.equity)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="grid w-full gap-4">
          <DashboardPinnedWidget
            title={showPortfolio ? 'All Accounts - Open Positions' : 'Open Positions'}
            description="Live positions and trade-management actions."
          >
            {renderOpenPositionsWidget()}
          </DashboardPinnedWidget>

          <DashboardPinnedWidget
            title={showPortfolio ? 'All Accounts - Pending Orders' : 'Pending Orders'}
            description="Orders waiting to trigger."
          >
            {renderPendingOrdersWidget()}
          </DashboardPinnedWidget>
        </div>

        </>
        )}

        <DashboardWidgetCanvas
          selectedAccountId={!showPortfolio ? selectedAccountId || null : null}
          showPortfolio={showPortfolio}
          openPositions={widgetPositions}
          balance={widgetBalance}
          equity={widgetEquity}
          headerContent={dashboardHeaderContent}
          mobileHeaderContent={dashboardMobileHeaderContent}
          renderNeedsReviewWidget={() => <DashboardActivityPanel bare />}
          renderBalanceWidget={renderBalanceWidget}
          renderOpenPositionsWidget={renderOpenPositionsWidget}
          renderPendingOrdersWidget={renderPendingOrdersWidget}
        />
      </main>

      {/* ── Modals ─────────────────────────────────────────────── */}
      {selectedTradeForSlUpdate && (
        <UpdateStopLossModal
          isOpen={isUpdateSlModalOpen}
          onClose={handleCloseUpdateSlModal}
          trade={selectedTradeForSlUpdate}
          onSuccessfulUpdate={() => handleTradeActionSuccess(selectedTradeForSlUpdate?.id)}
        />
      )}

      {selectedTradeForPartialClose && (
        <PartialCloseModal
          isOpen={isPartialCloseModalOpen}
          onClose={handleClosePartialCloseModal}
          trade={selectedTradeForPartialClose}
          onSuccessfulPartialClose={() => handleTradeActionSuccess(selectedTradeForPartialClose?.id)}
        />
      )}

      {selectedTradeForTpUpdate && (
        <UpdateTakeProfitModal
          isOpen={isUpdateTpModalOpen}
          onClose={handleCloseUpdateTpModal}
          trade={selectedTradeForTpUpdate}
          onSuccessfulTpUpdate={() => handleTradeActionSuccess(selectedTradeForTpUpdate?.id)}
        />
      )}

      <ConfirmModal
        open={!!confirmAction}
        onClose={() => !confirmLoading && setConfirmAction(null)}
        onConfirm={executeConfirmAction}
        loading={confirmLoading}
        title={confirmTitle}
        message={confirmMessage}
        confirmLabel={confirmLabel}
        confirmVariant="danger"
      />
    </div>
  );
};

export default Dashboard;
